import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdDelete, MdEdit, MdCheck, MdClose, MdSearch } from 'react-icons/md';

function ManageCategoriesSection() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fields = [
    { key: 'name', placeholder: 'Category name (e.g. Markets)', required: true },
    { key: 'slug', placeholder: 'Slug (English only, e.g. markets)', required: true },
    { key: 'description', placeholder: 'Description (optional)', required: false },
    { key: 'show_on_home', placeholder: 'Show on home page', type: 'checkbox', label: 'On Home' },
  ];

  const fetchItems = async () => {
    try {
      const res = await api.get('/categories');
      setItems(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search') || searchParams.get('q') || '';
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
    }
  }, [location.search]);

  const handleAiGenerate = async (isEdit = false) => {
    const sourceName = isEdit ? editForm.name : form.name;
    if (!sourceName) {
      return toast.error('Please enter a category name first');
    }

    setAiLoading(true);
    try {
      const res = await api.post('/admin/ai/generate', { type: 'slug', title: sourceName });
      if (res.data.success) {
        if (isEdit) {
          setEditForm(prev => ({ ...prev, slug: res.data.data }));
        } else {
          setForm(prev => ({ ...prev, slug: res.data.data }));
        }
        toast.success('Slug generated');
      }
    } catch (err) {
      toast.error('AI generation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/categories', form);
      toast.success('Category added');
      setForm({});
      setShowForm(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add category');
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/categories/${deleteModal.id}`);
      toast.success('Category deleted');
      setDeleteModal(null);
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    const ef = {};
    fields.forEach(f => { ef[f.key] = item[f.key] || ''; });
    setEditForm(ef);
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/categories/${id}`, editForm);
      toast.success('Category updated');
      setEditingId(null);
      fetchItems();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const filteredItems = items.filter(item => {
    const q = searchQuery.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.slug || '').toLowerCase().includes(q) ||
      (item.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="manage-section">
      <div className="section-header">
        <h3>Categories</h3>
        <div className="flex-row items-center gap-2">
          <div className="search-input-wrap" style={{ maxWidth: '240px' }}>
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search category..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add Category</>}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="manage-form mb-4" onSubmit={handleCreate}>
          {fields.map(f => (
            f.type === 'checkbox' ? (
              <label key={f.key} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form[f.key] || false}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.checked }))}
                />
                {f.placeholder}
              </label>
            ) : (
              <div key={f.key} className="form-group mb-2">
                {f.key === 'slug' && (
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0 text-xs font-bold uppercase text-muted">Slug</label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate(false)} disabled={aiLoading}>
                      ✨ AI Slug
                    </button>
                  </div>
                )}
                <input
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.key] || ''}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  required={f.required}
                />
              </div>
            )
          ))}
          <button type="submit" className="btn btn-sm btn-primary" disabled={aiLoading}>Save Category</button>
        </form>
      )}

      {loading ? <p className="text-muted">Loading categories...</p> : (
        <div className="manage-list">
          {filteredItems.map(item => (
            <div key={item.id} className="manage-item">
              {editingId === item.id ? (
                <div className="manage-edit-row">
                  {fields.map(f => (
                    f.type === 'checkbox' ? (
                      <label key={f.key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={editForm[f.key] || false}
                          onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.checked }))}
                        />
                        {f.placeholder}
                      </label>
                    ) : (
                      <div key={f.key} className="form-group mb-2">
                        {f.key === 'slug' && (
                          <div className="flex-row justify-between items-center mb-1">
                            <label className="mb-0 text-xs font-bold uppercase text-muted">Slug</label>
                            <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate(true)} disabled={aiLoading}>
                              ✨ AI Slug
                            </button>
                          </div>
                        )}
                        <input
                          type={f.type || 'text'}
                          value={editForm[f.key] || ''}
                          onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        />
                      </div>
                    )
                  ))}
                  <div className="flex-row gap-2 mt-2">
                    <button className="btn btn-sm btn-primary" onClick={() => saveEdit(item.id)} disabled={aiLoading}><MdCheck /></button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditingId(null)}><MdClose /></button>
                  </div>
                </div>
              ) : (
                <div className="manage-display-row">
                  <div className="manage-item-info">
                    <span className="manage-name">{item.name}</span>
                    <span className="manage-slug">{item.slug}</span>
                    {item.show_on_home && <span className="badge badge-success ml-2">On Home</span>}
                  </div>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => startEdit(item)}><MdEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(item)}><MdDelete /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filteredItems.length === 0 && <p className="text-muted">No categories found</p>}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && createPortal(
        <div 
          className="modal-overlay" 
          onClick={() => setDeleteModal(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 999999, padding: '20px', boxSizing: 'border-box'
          }}
        >
          <div 
            className="modal modal-sm" 
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '440px', width: '100%', backgroundColor: '#ffffff',
              borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              overflow: 'hidden', margin: 'auto', display: 'flex', flexDirection: 'column'
            }}
          >
            <div className="modal-header" style={{ padding: '18px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>Delete Category</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)} style={{ background: '#F3F4F6', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4B5563' }}>
                <MdClose size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#111827', lineHeight: '1.5' }}>
                Are you sure you want to delete the category <strong>"{deleteModal.name}"</strong>?
              </p>
              <p className="text-muted" style={{ margin: '8px 0 0 0', fontSize: '13px' }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)} style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, height: '38px', margin: 0 }}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting} style={{ padding: '8px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, backgroundColor: '#EF233C', color: '#ffffff', border: 'none', height: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function ManageCategories() {
  return (
    <div className="page">
      <div className="page-header">
        <h2>Manage Categories</h2>
      </div>
      <div className="card" style={{ maxWidth: '900px' }}>
        <ManageCategoriesSection />
      </div>
    </div>
  );
}
